import React from 'react';
import { RoomQueue } from '@/domains/internal/dashboard/services/dashboardService';
import { Button } from '@/shared/components/ui/Button';

interface RoomCardProps {
  room: RoomQueue;
  departmentCode: string;
}

export const RoomCard: React.FC<RoomCardProps> = ({ room }) => {
  // Get current serving (inprogress) and waiting entries
  const currentServing = room.test_entries.find(entry => entry.status === 'inprogress');
  const waitingEntries = room.test_entries.filter(entry => entry.status === 'waiting').sort((a, b) => a.queue_position - b.queue_position);
  const totalWaiting = room.test_entries.filter(entry => entry.status === 'waiting' || entry.status === 'called').length;
  const isActive = room.test_entries.some(entry => entry.status === 'inprogress');

  return (
    <div className="bg-white box-border content-stretch flex flex-col gap-[40px] items-center justify-center p-[20px] relative rounded-[6px] shadow-[0px_3px_12px_0px_rgba(61,49,43,0.14)] w-full">
      <div className="content-stretch flex items-center relative shrink-0 w-full">
        <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-start min-h-px min-w-px relative shrink-0">
          <p className="font-['Roboto'] font-medium leading-[20px] relative shrink-0 text-[var(--color-text-primary)] text-[16px] tracking-[0.1px] w-full" style={{ fontVariationSettings: "'wdth' 100" }}>
            {room.room_name}
          </p>
          <div className="content-stretch flex gap-[4px] items-center opacity-60 relative shrink-0 w-full">
            <p className="font-['Roboto'] font-normal leading-[20px] relative shrink-0 text-[var(--color-text-primary)] text-[14px] text-nowrap tracking-[0.25px] whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
              {room.room_number}
            </p>
            <div className="relative shrink-0 size-[4px]">
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            </div>
            <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
              <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
                <div className="relative shrink-0 size-[16px]">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="6" r="3" stroke="#666" strokeWidth="1"/>
                    <path d="M4 14c0-2.5 2-4 4-4s4 1.5 4 4" stroke="#666" strokeWidth="1" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <p className="font-['Roboto'] font-normal leading-[20px] relative shrink-0 text-[var(--color-text-primary)] text-[14px] text-nowrap tracking-[0.25px] whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
                {room.technician}
              </p>
            </div>
          </div>
        </div>
        <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
          <div className="content-stretch flex flex-col items-start justify-center relative shrink-0">
            <div className={`${isActive ? 'bg-[rgba(40,199,111,0.16)]' : 'bg-[rgba(156,163,175,0.16)]'} box-border content-stretch flex gap-[4px] items-center justify-center min-w-[32px] px-[12px] py-[6px] relative rounded-[6px] shrink-0`}>
              <div className={`basis-0 flex flex-col font-['Roboto'] font-medium grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 ${isActive ? 'text-[#28c76f]' : 'text-[#9ca3af]'} text-[13px] text-center`} style={{ fontVariationSettings: "'wdth' 100" }}>
                <p className="leading-[20px]">{isActive ? 'Active' : 'Available'}</p>
              </div>
            </div>
          </div>
          <div className={`flex flex-col font-['Roboto'] font-medium justify-center leading-[0] relative shrink-0 ${isActive ? 'text-[#28c76f]' : 'text-[#9ca3af]'} text-[13px] text-nowrap`} style={{ fontVariationSettings: "'wdth' 100" }}>
            <p className="leading-[20px] whitespace-pre">{totalWaiting}/{totalWaiting + 2}</p>
          </div>
        </div>
      </div>

      <div className="bg-[#e4edff] box-border content-stretch flex flex-col items-center p-[24px] relative rounded-[6px] shrink-0 w-full">
        <p className="font-['Roboto'] font-normal leading-[24px] min-w-full opacity-60 relative shrink-0 text-[#4a90e2] text-[16px] text-center tracking-[0.5px]" style={{ width: "min-content", fontVariationSettings: "'wdth' 100" }}>
          NOW SERVING
        </p>
        <p className="font-['Roboto'] font-semibold leading-[40px] relative shrink-0 text-[#204876] text-[36px] text-center text-nowrap whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
          {currentServing?.patient_call_number || '-'}
        </p>
        <div className="content-stretch flex gap-[8px] items-center justify-center relative shrink-0 w-full">
          <div className="relative shrink-0 size-[20px]">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="#4a90e2" strokeWidth="1.5"/>
              <path d="M10 6v4l3 2" stroke="#4a90e2" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <p className="font-['Roboto'] font-normal leading-[24px] relative shrink-0 text-[#4a90e2] text-[16px] text-center text-nowrap tracking-[0.5px] whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
            Est: {currentServing?.estimated_wait_minutes || 0} mins
          </p>
        </div>
      </div>

      <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
        <p className="font-['Roboto'] font-normal leading-[20px] relative shrink-0 text-[var(--color-text-primary)] text-[16px] tracking-[0.25px] w-full" style={{ fontVariationSettings: "'wdth' 100" }}>
          NEXT
        </p>
        <div className="content-center flex flex-wrap gap-[8px] items-center relative shrink-0 w-full">
          {waitingEntries.slice(0, 5).map((entry, index) => (
            <div key={entry.id} className={`${index === 0 ? 'bg-[#f8faf0]' : 'bg-white'} content-stretch flex flex-col items-start relative shrink-0`}>
              <Button variant="neutral" className="!box-border !content-stretch !cursor-pointer !flex !flex-col !items-start !justify-center !overflow-visible !p-0 !relative !shrink-0 !bg-transparent !border-none !rounded-none">
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0">
                  <div className="box-border content-stretch flex gap-[10px] items-center px-[12px] py-[4px] relative rounded-[4px] shrink-0 w-full border border-[rgba(47,43,61,0.22)]">
                    <div className="content-stretch flex flex-col items-start justify-center relative shrink-0">
                      <div className="content-stretch flex gap-[10px] items-start relative shrink-0">
                        <div className={`flex flex-col font-['Roboto'] font-normal justify-center leading-[0] relative shrink-0 text-[14px] ${index === 0 ? 'text-[#643e00]' : 'text-[rgba(47,43,61,0.9)]'} text-nowrap tracking-[0.25px]`} style={{ fontVariationSettings: "'wdth' 100" }}>
                          <p className="leading-[20px] whitespace-pre">{entry.patient_call_number || '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0">
        <div className="content-stretch flex gap-[13px] items-start relative shrink-0 w-full">
          <Button variant="info" className="!bg-[#4a90e2] !flex !items-center !justify-center !gap-2 !px-6 !py-3 !rounded-md !flex-1 !h-12" padding="px-6 py-3" fontSize="text-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
              <path d="M5 3l6 5-6 5V3z"/>
            </svg>
            <span className="font-semibold text-white text-sm">Next</span>
          </Button>
          <Button variant="neutral" className="!bg-white !border-2 !border-gray-300 !flex !items-center !justify-center !gap-2 !px-6 !py-3 !rounded-md !flex-1 !h-12" padding="px-6 py-3" fontSize="text-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="4" y="2" width="2" height="12" fill="#9ca3af" rx="1"/>
              <rect x="10" y="2" width="2" height="12" fill="#9ca3af" rx="1"/>
            </svg>
            <span className="font-semibold text-gray-500 text-sm">Pause</span>
          </Button>
        </div>
        <div className="content-stretch flex gap-[13px] items-start relative shrink-0 w-full">
          <Button variant="success" className="!bg-white !border-2 !border-green-500 !flex !items-center !justify-center !gap-2 !px-6 !py-3 !rounded-md !flex-1 !h-12" padding="px-6 py-3" fontSize="text-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" fill="#22c55e"/>
              <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="font-semibold text-green-500 text-sm">Done</span>
          </Button>
          <Button variant="info" className="!bg-white !border-2 !border-blue-500 !flex !items-center !justify-center !gap-2 !px-6 !py-3 !rounded-md !flex-1 !h-12" padding="px-6 py-3" fontSize="text-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="5" r="2.5" stroke="#4a90e2" strokeWidth="1.5"/>
              <path d="M3 14c0-2.5 2.5-4 5-4s5 1.5 5 4" stroke="#4a90e2" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span className="font-semibold text-blue-500 text-sm">Admin</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
